import type { MonthlyStatement, Transaction } from '../hooks/useAccountingData';
import { todayString } from '../../../utils/dateUtils';
import { downloadBytes } from '../../../utils/download';
import { buildXlsx, XLSX_MIME, type XlsxColumn } from '../../../utils/xlsx';

/**
 * 核算页两份台账：月报与收支明细。
 *
 * 换掉 CSV 的理由在这里最实在——这两张表全是金额，CSV 里它们是不带类型的字符串，
 * 换一台机器、换一个区域设置就可能被当成文本，拉直了求和得出 0。
 * 现在金额写数字格并带 `#,##0.00`，落到 Excel 里直接就是能汇总的数；
 * 月份和活动日期写文本格，不会被区域设置重排成 2026/10/9。
 */

/** 文件名尾巴：带筛选区间就并进名字里，否则只有日期，两份产物分不出是谁 */
function filenameBase(base: string, dateRange?: string): string {
  const suffix = dateRange ? `_${dateRange}` : '';
  return `${base}${suffix}_${todayString()}.xlsx`;
}

/**
 * 导出月报（末行是这批数据的合计）
 */
export function exportMonthlyStatementXlsx(rows: MonthlyStatement[], dateRange?: string): void {
  const columns: XlsxColumn[] = [
    { header: '月份', width: 12 },
    { header: '收入(元)', width: 14, kind: 'money' },
    { header: '支出(元)', width: 14, kind: 'money' },
    { header: '净利润(元)', width: 14, kind: 'money' },
    { header: '订单数', width: 11, kind: 'number' },
    { header: '已收(元)', width: 14, kind: 'money' },
    { header: '待收(元)', width: 14, kind: 'money' },
  ];
  // 待收逐单夹零，不等于「收入 − 已收」：存量脏单（实收越过应收）待收记 0，那笔差额无处可去
  const toRow = (r: Pick<MonthlyStatement, 'income' | 'expense' | 'profit' | 'orders' | 'paidAmount' | 'pendingAmount'>, month: string) => [
    month,
    r.income,
    r.expense,
    r.profit,
    r.orders,
    r.paidAmount,
    r.pendingAmount,
  ];

  const body = rows.map((r) => toRow(r, r.month));
  const sum = rows.reduce(
    (acc, r) => ({
      income: acc.income + r.income,
      expense: acc.expense + r.expense,
      profit: acc.profit + r.profit,
      orders: acc.orders + r.orders,
      paidAmount: acc.paidAmount + r.paidAmount,
      pendingAmount: acc.pendingAmount + r.pendingAmount,
    }),
    { income: 0, expense: 0, profit: 0, orders: 0, paidAmount: 0, pendingAmount: 0 },
  );
  body.push(toRow(sum, '合计'));

  downloadBytes(
    filenameBase('月度报表', dateRange),
    buildXlsx({ sheetName: '月度报表', columns, rows: body }),
    XLSX_MIME,
  );
}

/**
 * 导出收支明细
 */
export function exportTransactionsXlsx(rows: Transaction[], dateRange?: string): void {
  const columns: XlsxColumn[] = [
    { header: '订单号', width: 19 },
    { header: '类型' },
    { header: '客户', width: 13 },
    { header: '服务类型', width: 13 },
    { header: '活动日期', width: 12 },
    { header: '订单金额(元)', width: 14, kind: 'money' },
    { header: '已收金额(元)', width: 14, kind: 'money' },
    { header: '未收金额(元)', width: 14, kind: 'money' },
    { header: '支付方式', width: 13 },
    { header: '付款状态', width: 13 },
    { header: '订单状态', width: 13 },
    { header: '审核状态', width: 13 },
  ];
  const body = rows.map((r) => [
    r.orderNumber ?? '',
    (r.orderCategory ?? '').includes('支出') ? '支出' : '收入',
    r.customerName ?? '',
    r.serviceTypeName ?? '',
    r.orderStartDate ? r.orderStartDate.slice(0, 10) : '',
    r.orderAmount,
    r.paidAmount,
    r.unpaidAmount,
    r.paymentMethod ?? '',
    r.paymentStatus ?? '',
    r.orderStatus ?? '',
    r.reviewStatus ?? '',
  ]);

  downloadBytes(
    filenameBase('收支明细', dateRange),
    buildXlsx({ sheetName: '收支明细', columns, rows: body }),
    XLSX_MIME,
  );
}
